import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { formatCountdown } from '../lib/format';
import { useI18n } from '../lib/i18n';

/**
 * §37.3 — the SLA countdown.
 *
 * The SERVER is the source of truth: it hands over `secondsRemaining` measured
 * against its own clock, plus the instant it measured at. The client only ticks
 * that number down for display. A workstation with a wrong clock therefore shows
 * the right countdown, and nothing here ever decides whether an SLA was met.
 */
export function SlaCountdown({
  secondsRemaining,
  measuredAt,
  label,
  met,
  stopped = false,
  processed,
  size = 'md',
}: {
  secondsRemaining: number;
  /** The server timestamp the value was measured at, so drift is corrected. */
  measuredAt?: string | Date;
  label?: string;
  /** Known outcome: true = met, false = missed, undefined = still running. */
  met?: boolean | null;
  /** The ticket was cancelled: a clock with no outcome stopped with it, and counts no further. */
  stopped?: boolean;
  /** The deadline job has already run. */
  processed?: boolean;
  size?: 'sm' | 'md';
}) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());

  const measuredMs = measuredAt ? new Date(measuredAt).getTime() : null;
  const elapsed = measuredMs ? (now - measuredMs) / 1000 : 0;
  const remaining = secondsRemaining - elapsed;
  const settled = (met !== undefined && met !== null) || stopped;

  useEffect(() => {
    // Stop ticking once it is settled — a finished countdown is not live data.
    if (settled) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [settled]);

  const resolved: 'met' | 'missed' | 'stopped' | 'running' | 'expired' =
    met === true
      ? 'met'
      : met === false
        ? 'missed'
        : stopped
          ? 'stopped'
          : remaining <= 0
            ? 'expired'
            : 'running';

  // Under a minute is the warning band; past zero is a breach. The light theme
  // takes the tokens' dark text on their own pale fill, as the badges do: these
  // sit in list rows, and a hovered row turns a tint too dark under them.
  const tone =
    resolved === 'met'
      ? 'bg-success-bg text-success-fg ring-success/30 dark:bg-success/10 dark:text-success'
      : resolved === 'missed' || resolved === 'expired'
        ? 'bg-error-bg text-error-fg ring-error/30 dark:bg-error/10 dark:text-error-dark'
        : resolved === 'stopped'
          ? 'text-[var(--text-secondary)] ring-neutral-500/25 bg-neutral-500/12'
          : remaining <= 60
            ? 'bg-warning-bg text-warning-fg ring-warning/35 dark:bg-warning/12 dark:text-warning-dark animate-pulse-signal'
            : 'bg-info-bg text-info-fg ring-info/30 dark:bg-info/10 dark:text-info-dark';

  const text =
    resolved === 'met'
      ? t('tickets.slaMet')
      : resolved === 'missed'
        ? t('tickets.slaMissed')
        : resolved === 'stopped'
          ? t('tickets.slaStopped')
          : formatCountdown(remaining);

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-pill ring-1 ring-inset font-mono tabular whitespace-nowrap',
        size === 'sm' ? 'px-2 py-0.5 text-mono-sm' : 'px-2.5 py-1 text-mono',
        tone,
      )}
      title={processed ? 'The deadline job has run.' : undefined}
    >
      {label && <span className="font-sans text-caption">{label}</span>}
      {text}
    </span>
  );
}

/** A compact pair of both clocks, for list rows. */
export function SlaPair({
  viewSeconds,
  assignmentSeconds,
  serverNow,
  viewMet,
  assignmentMet,
  stopped = false,
  compact = false,
}: {
  viewSeconds: number;
  assignmentSeconds: number;
  serverNow?: string;
  viewMet?: boolean | null;
  assignmentMet?: boolean | null;
  stopped?: boolean;
  compact?: boolean;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <SlaCountdown
        secondsRemaining={viewSeconds}
        measuredAt={serverNow}
        label={compact ? 'V' : t('tickets.viewSla')}
        met={viewMet}
        stopped={stopped}
        size="sm"
      />
      <SlaCountdown
        secondsRemaining={assignmentSeconds}
        measuredAt={serverNow}
        label={compact ? 'A' : t('tickets.assignmentSla')}
        met={assignmentMet}
        stopped={stopped}
        size="sm"
      />
    </div>
  );
}
